"use strict";

/*
 * kzm-i18n.js — the calendar's strings, from Pimcore instead of from the bundle.
 *
 * Ships to `web/static/js/libs/local/kzmCal/kzm-i18n.js`. A plain ES module, not
 * an IIFE global: the host bundler already resolves `../libs/local/kzmCal/…` from
 * both search bars, so this gets inlined at build time — no extra <script> tag,
 * no new global, no load-order question. It is not minified separately for the
 * same reason the two search bars are not.
 *
 * Before this file, both search bars carried the same ternary:
 *
 *   var isDe = !dataLocale || dataLocale === "de";
 *   var lang = isDe ? "de-DE" : "en-US";
 *   var rangeLocale = isDe ? { one: "Nacht", … } : { one: "Night", … };
 *
 * which meant exactly two languages, a duplicated table that had already drifted
 * (the start page never passed `departureOnlyLabel`), and German as the silent
 * fallback for any locale the branch did not recognise.
 *
 * The strings now arrive as a JSON blob on the container the modules already
 * read, alongside `data-locale` and `data-footer-info`:
 *
 *   <div class="js-easypicker-container"
 *        data-locale="de"
 *        data-i18n='{"minStay":"Mindestaufenthalt","maxStay":"Max. Aufenthalt",
 *                    "unavailable":"Nicht verfügbar",
 *                    "departureOnly":"Nur als Abreisetag",
 *                    "nights.one":"Nacht","nights.other":"Nächte"}'>
 *
 * Twig side — single-quote the attribute, `json_encode` emits double quotes, and
 * do NOT add `|raw`: Twig's escaping is what keeps a translation containing an
 * apostrophe or `<` from breaking out of the attribute, and `.data()` un-escapes
 * it on read.
 *
 *   data-i18n='{{ {
 *       "minStay":       "calendar.min_stay"|trans,
 *       "maxStay":       "calendar.max_stay"|trans,
 *       "unavailable":   "calendar.unavailable"|trans,
 *       "departureOnly": "calendar.departure_only"|trans,
 *       "nights.one":    "calendar.nights.one"|trans,
 *       "nights.other":  "calendar.nights.other"|trans
 *   }|json_encode }}'
 *
 * Adding a language is then a catalogue entry plus a locale — no JS deploy.
 */

/*
 * The pre-Pimcore strings, kept as a last resort so a container whose Twig has
 * not been updated yet renders what it rendered before this change instead of
 * blank labels. An empty `minStay` produces " 29 Nächte", which is a worse
 * failure than a stale word.
 *
 * DELETE THIS TABLE once every template emits `data-i18n`. It is migration
 * scaffolding, not a feature — leaving it in place indefinitely re-creates the
 * exact problem this file exists to solve, just one level down.
 */
var FALLBACK = {
    de: {
        minStay: "Mindestaufenthalt",
        maxStay: "Max. Aufenthalt",
        unavailable: "Nicht verfügbar",
        departureOnly: "Nur als Abreisetag",
        nights: { one: "Nacht", other: "Nächte" },
    },
    en: {
        minStay: "Min. stay",
        maxStay: "Max. stay",
        unavailable: "Unavailable",
        departureOnly: "Departure only",
        nights: { one: "Night", other: "Nights" },
    },
};

var DEFAULT_LOCALE = "de-DE";

/* Unwraps a jQuery object, a jQuery-like, or a bare element. */
function toEl(el) {
    if (!el) return null;
    if (el.nodeType === 1) return el;
    if (el.jquery || (typeof el.length === "number" && el[0])) return el[0] || null;
    return null;
}

function attr(el, name) {
    var node = toEl(el);
    return node && node.getAttribute ? node.getAttribute(name) : null;
}

/**
 * The BCP-47 tag handed to `Intl` for month and weekday names.
 *
 * `data-locale` → `<html lang>` → `de-DE`. Pimcore emits `de` or `de_AT`; the
 * underscore is normalised and the tag goes straight to `Intl`, which resolves
 * `de` to German on its own. That pass-through is what lets a third language
 * work with no change here — the old two-branch lookup could not.
 */
export function readLocale(el) {
    var raw = attr(el, "data-locale");

    if (!raw && typeof document !== "undefined" && document.documentElement) {
        raw = document.documentElement.getAttribute("lang");
    }
    if (!raw) return DEFAULT_LOCALE;

    var tag = String(raw).trim().replace(/_/g, "-");
    if (!tag) return DEFAULT_LOCALE;

    // A tag Intl cannot parse would throw from deep inside KzmCal's constructor,
    // where the stack says nothing about a bad attribute. Check it here instead.
    try {
        new Intl.DateTimeFormat(tag);
    } catch (e) {
        return DEFAULT_LOCALE;
    }
    return tag;
}

/* Picks the fallback bundle for a tag: `de-AT` and `de` both mean German. */
function fallbackFor(tag) {
    var base = String(tag || "").toLowerCase().split("-")[0];
    return FALLBACK[base] || FALLBACK.en;
}

/**
 * The calendar's wording for one container.
 *
 * Returns `{ minStay, maxStay, unavailable, departureOnly, nights: { one, other } }`,
 * shaped to drop straight into KzmCal's `minStayLabel` / `maxStayLabel` /
 * `unavailableLabel` / `departureOnlyLabel` / `nightsLocale` options.
 *
 * Every key is guaranteed present: a partial blob fills its gaps from the
 * fallback, so a half-translated catalogue degrades one word at a time instead
 * of rendering `undefined` into the tooltip.
 */
export function readI18n(el, locale) {
    var base = fallbackFor(locale || readLocale(el));
    var out = {
        minStay: base.minStay,
        maxStay: base.maxStay,
        unavailable: base.unavailable,
        departureOnly: base.departureOnly,
        nights: { one: base.nights.one, other: base.nights.other },
    };

    var blob = parseBlob(attr(el, "data-i18n"));
    if (!blob) return out;

    // Dotted keys keep the catalogue readable in the Pimcore translation UI —
    // `calendar.nights.one` reads better there than a nested object would.
    assign(out, "minStay", blob.minStay);
    assign(out, "maxStay", blob.maxStay);
    assign(out, "unavailable", blob.unavailable);
    assign(out, "departureOnly", blob.departureOnly);

    var nights = blob.nights || {};
    assign(out.nights, "one", blob["nights.one"] !== undefined ? blob["nights.one"] : nights.one);
    assign(out.nights, "other", blob["nights.other"] !== undefined ? blob["nights.other"] : nights.other);

    return out;
}

/* Only non-empty strings win; `""` and `null` fall through to the fallback. */
function assign(target, key, value) {
    if (typeof value === "string" && value.trim() !== "") {
        target[key] = value;
    }
}

/*
 * A malformed blob must never throw: a broken translation is a cosmetic problem,
 * a constructor that throws takes the whole calendar down and the user cannot
 * book at all. Parse failures fall back silently in the return value and say so
 * once on the console.
 */
function parseBlob(raw) {
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    try {
        var parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
        if (typeof console !== "undefined" && console.warn) {
            console.warn("kzm-i18n: data-i18n is not valid JSON, using built-in labels —", raw);
        }
        return null;
    }
}
